import { ComponentType, useContext, useState } from 'react';
import {
  Window,
  WindowActionsBar,
  WindowProps
} from '@progress/kendo-react-dialogs';
import { FluidForm } from '../../../components/forms';
import { AreaEntity, ResourceEntity } from '../../../types/game-document/';
import {
  CheckboxChangeEvent,
  InputChangeEvent,
  SwitchChangeEvent,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { EditorMode } from '../../../types/editor-mode';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { AreaEditor, AreaEditorFeature } from '../areas';
import { GameDocumentContext } from '../../../contexts/game-document';
import { Algorithm } from '../../../types/algorithm';
import cloneDeep from 'lodash.clonedeep';
import { AreaEvent } from '../../../types/game-document/entities/area';
import { AreaAlgorithmEvent } from '../../../types/game-document/area';
import RequiredFields from '../../../types/required-fields';
import { GetResourceValue } from '../../../utils/game-document';

interface AreaEditorWindowProps extends WindowProps {
  editorEntity: EntityEditor<AreaEntity>;
  editorResource: EntityEditor<ResourceEntity>[];
  toggleDialog: Function;
  onSubmit: (
    editorEntity: EntityEditor<AreaEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => void;
  editorMode: EditorMode;
}

export const AreaEditorWindow: ComponentType<AreaEditorWindowProps> = ({
  editorEntity,
  toggleDialog,
  editorResource,
  onSubmit,
  editorMode,
  ...props
}: AreaEditorWindowProps) => {
  const [state] = useContext(GameDocumentContext);
  const [entity, setEntity] = useState<AreaEntity>(
    cloneDeep(editorEntity.entity)
  );
  const [resources, setResources] = useState<ResourceEntity[]>([
    ...editorResource.map((title) => title.entity)
  ]);

  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<AreaEntity>[]
  >([{ name: 'titleResId', errorMessage: '' }]);

  const handleInputChange = (
    event:
      | InputChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | SwitchChangeEvent
  ) => {
    const { name, value } = event.target;

    if (name === 'titleResId') {
      setResources(
        resources.map((item) =>
          item.id === entity![name]
            ? { ...item, value: value! as string }
            : { ...item }
        )
      );
    } else {
      setEntity({ ...entity, [name!]: value });
    }
  };

  const isInputValid = (): boolean => {
    const inputValidation = requiredFields.map(
      (input: RequiredFields<AreaEntity>) => {
        const titleRes = resources.find(
          (r) => r.id === editorEntity.entity.titleResId
        );

        input.errorMessage = '';
        if (input.name === 'titleResId' && titleRes !== undefined) {
          // Check validation for input title

          if (titleRes.value === '') {
            input.errorMessage = 'This field is required';
          }
        }

        return input;
      }
    );
    setRequiredFields(inputValidation);
    return (
      inputValidation.findIndex((input) => input.errorMessage !== '') === -1
    );
  };

  const handleAlgorithmEventChange = (
    algorithm: Algorithm,
    type: AreaAlgorithmEvent
  ) => {
    const copyEntity = cloneDeep({ ...entity });
    if (copyEntity.events === undefined) {
      copyEntity.events = {
        onEntry: {} as Algorithm,
        onExit: {} as Algorithm
      };
    }
    copyEntity.events[type as keyof AreaEvent] = algorithm;
    setEntity((prev) => ({ ...prev, ...copyEntity }));
  };

  return (
    <Window
      modal={true}
      minimizeButton={undefined}
      className={'shadow'}
      initialWidth={editorEntity.isNew ? 500 : 700}
      initialHeight={editorEntity.isNew ? 450 : 650}
      title={
        editorEntity.isNew
          ? 'New area'
          : GetResourceValue(state.gameDocument!, entity.titleResId ?? '')
      }
      {...props}>
      <FluidForm>
        {editorEntity.isNew ? (
          <AreaEditor
            editorMode={editorMode}
            entity={entity}
            resources={resources}
            handleInputChange={handleInputChange}
            requiredFields={requiredFields}
          />
        ) : (
          <AreaEditorFeature
            entity={entity}
            resources={resources}
            handleInputChange={handleInputChange}
            handleAlgorithmEventChange={handleAlgorithmEventChange}
            requiredFields={requiredFields}
          />
        )}
      </FluidForm>
      <WindowActionsBar>
        <Button themeColor={'secondary'} onClick={() => toggleDialog()}>
          Cancel
        </Button>
        <Button
          themeColor={'primary'}
          onClick={() => {
            if (isInputValid()) {
              const titleSetting =
                state.gameDocument?.settings.designer
                  .defaultAreaTitleVisibility ?? false;
              const entityResources = resources.map((item, index) => ({
                isNew: editorResource[index].isNew,
                entity: item
              })) as EntityEditor<ResourceEntity>[];
              onSubmit(
                {
                  isNew: editorEntity.isNew,
                  entity: {
                    ...entity,
                    ...(editorEntity.isNew && {
                      isTitleVisible: titleSetting
                    })
                  }
                },
                entityResources
              );
            }
          }}>
          Save
        </Button>
      </WindowActionsBar>
    </Window>
  );
};
